import { useContext, useEffect } from 'react'

import {
  Button, Form, ModalContext, SidebarModal,
} from '@domain-group/suit-theme'

import { digObject, toggleArray } from '@domain-group/javascript-utils'

import { useForm, UseFormPayload } from '@domain-group/react-hooks'

import useSystem from '@hooks/useSystem'

import defaultFormState, { requiredFields } from '@models/dataStoreItemKey'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

const MODAL_KEY = 'CreateOrEditDataStoreItemKeyModal'

type ModalCallbacks = {
  closeModal: () => void,
  showModal: boolean,
  updateSystem: (payload: HandleCallbackActionParams) => Promise<object>,
}

type CreateOrEditItemKeyProps = {
  callbacks: ModalCallbacks,
  dataStoreItemKeys: [],
  isEditing: boolean,
  options: object,
  entityState: UseFormPayload['entityState'],
  updateFn: Function,
}

const createOrEditItemKey = (params: CreateOrEditItemKeyProps) => {
  const {
    callbacks: { closeModal, updateSystem }, dataStoreItemKeys, entityState, isEditing, options, updateFn,
  } = params

  const updatedDataStoreItemKeys = toggleArray(dataStoreItemKeys, entityState, { useObjectKey: 'id' })

  const updatedSystem = {
    options: {
      ...options,
      data_store_item_keys: updatedDataStoreItemKeys,
    },
  }

  const actionText = isEditing ? 'Updated' : 'Created'

  const payload = {
    callbacks: {
      action: updateFn,
      afterAction: closeModal,
    },
    entityParams: updatedSystem,
    toastText: `Data Store Item Key ${actionText}`,
  }

  // @ts-expect-error TS2345
  updateSystem(payload)
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const CreateOrEditDataStoreItemKeyModal = (props: ModalProps) => {
  const { callbacks, showModal } = props

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const { dataStoreItemKey, system: initSystem } = modalPayload

  const initDataStoreItemKey = dataStoreItemKey || { id: new Date().getTime().toString(36) }

  const {
    callbacks: {
      updateSystem: updateFn,
    },
    system: { options },
    updating,
  } = useSystem(initSystem)

  const dataStoreItemKeys = digObject(options, 'data_store_item_keys', [])

  const formPayload = useForm(
    defaultFormState,
    { entity: initDataStoreItemKey, requiredFields, validateOn: 'change' },
    [dataStoreItemKey?.id],
  )

  const {
    entityState,
    entityState: { title, value },
    errors,
    handlers,
    saveEnabled,
    setEntityState,
  } = formPayload

  useEffect(() => {
    if (dataStoreItemKey){
      setEntityState({ title: dataStoreItemKey.title, value: dataStoreItemKey.value })
    }
  }, [dataStoreItemKey?.id])

  const isEditing = dataStoreItemKey?.id
  const updateOrCreateButtonText = isEditing ? 'Save Item Key' : 'Create Item Key'

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header callbacks={callbacks} title={isEditing ? 'Edit' : 'Add'} titleSecondLine="Item Key" />

      <SidebarModal.Content>
        <Form>
          <Form.Field label="* Title" errorMessage={errors.title}>
            <input name="title" value={title} {...handlers} />
          </Form.Field>

          <Form.Field label="* Key" marginTop="large" errorMessage={errors.value}>
            <input name="value" value={value} {...handlers} />
          </Form.Field>
        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveEnabled}
          loading={updating}
          onClick={() => createOrEditItemKey({
            callbacks, dataStoreItemKeys, entityState, isEditing, options, updateFn,
          })}
          size="large"
        >
          {updateOrCreateButtonText}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <CreateOrEditDataStoreItemKeyModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
